import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Copy, Users, DollarSign, Gift, CheckCircle,
  TrendingUp, Share2, Award, Zap,
} from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../utils/api';
import Layout from '../components/Layout';

const P  = '#C29500';
const P2 = '#E8C040';
const G  = '#E8C040';
const R  = '#EF4444';
const C  = '#06B6D4';

const CARD = {
  background: 'rgba(255,255,255,0.025)',
  border: '1px solid rgba(255,255,255,0.06)',
  borderRadius: 12,
};

const INNER = {
  background: 'rgba(255,255,255,0.03)',
  border: '1px solid rgba(255,255,255,0.06)',
  borderRadius: 10,
};

export default function Referral() {
  const [info,        setInfo]        = useState(null);
  const [commissions, setCommissions] = useState([]);
  const [loading,     setLoading]     = useState(true);
  const [copiedLink,  setCopiedLink]  = useState(false);
  const [copiedCode,  setCopiedCode]  = useState(false);
  const [activeTab,   setActiveTab]   = useState('overview');

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [infoRes, commRes] = await Promise.all([
        api.get('/referral/info'),
        api.get('/referral/commissions'),
      ]);
      setInfo(infoRes.data);
      setCommissions(commRes.data);
    } catch {
      toast.error('Failed to load referral data');
    } finally {
      setLoading(false);
    }
  };

  const copyLink = () => {
    navigator.clipboard.writeText(info?.referral_link || '');
    setCopiedLink(true);
    toast.success('Referral link copied!');
    setTimeout(() => setCopiedLink(false), 3000);
  };

  const copyCode = () => {
    navigator.clipboard.writeText(info?.referral_code || '');
    setCopiedCode(true);
    toast.success('Code copied!');
    setTimeout(() => setCopiedCode(false), 3000);
  };

  const shareLink = () => {
    if (navigator.share) {
      navigator.share({ title: 'Join Kudibot!', text: 'Accept stablecoins & pay out globally! Use my link:', url: info?.referral_link });
    } else {
      copyLink();
    }
  };

  if (loading) return (
    <Layout>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '60vh' }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{
            width: 36, height: 36, border: `3px solid ${P}`,
            borderTopColor: 'transparent', borderRadius: '50%',
            animation: 'spin .7s linear infinite', margin: '0 auto 12px',
          }}/>
          <p style={{ color: '#475569', fontSize: 14 }}>Loading referral data…</p>
        </div>
      </div>
    </Layout>
  );

  return (
    <Layout>
      <div style={{ padding: 28, maxWidth: 1100, margin: '0 auto', fontFamily: 'Inter, sans-serif' }}>

        {/* Header */}
        <motion.div initial={{ opacity: 0, y: -12 }} animate={{ opacity: 1, y: 0 }} style={{ marginBottom: 28 }}>
          <h1 style={{ fontSize: 22, fontWeight: 800, color: '#fff', margin: 0, letterSpacing: -0.5 }}>Referral Program</h1>
          <p style={{ fontSize: 13, color: '#64748B', margin: '6px 0 0' }}>
            Invite friends & earn{' '}
            <span style={{ color: P2, fontWeight: 700 }}>10% commission</span>
            {' '}on every subscription!
          </p>
        </motion.div>

        {/* Stats */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 14, marginBottom: 24 }} className="ref-stats">
          {[
            { label: 'Total Referrals', value: info?.total_referrals || 0,     color: C,  icon: <Users size={18}/>      },
            { label: 'Total Earned',    value: `$${info?.total_earned || 0}`,  color: G,  icon: <DollarSign size={18}/> },
            { label: 'Pending',         value: `$${info?.pending_earned || 0}`,color: P2, icon: <Zap size={18}/>        },
            { label: 'Commission Rate', value: info?.commission_rate || '10%', color: P,  icon: <Award size={18}/>      },
          ].map((s, i) => (
            <motion.div key={i}
              initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.07 }}
              style={{ ...CARD, padding: 18 }}>
              <div style={{
                width: 34, height: 34, borderRadius: 8, marginBottom: 10,
                background: `${s.color}14`, color: s.color,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>{s.icon}</div>
              <p style={{ fontSize: 11, color: '#64748B', margin: '0 0 4px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>{s.label}</p>
              <p style={{ fontSize: 22, fontWeight: 900, color: s.color, margin: 0 }}>{s.value}</p>
            </motion.div>
          ))}
        </div>

        {/* Referral Link Card */}
        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          style={{
            borderRadius: 12, padding: 28, marginBottom: 20, position: 'relative', overflow: 'hidden',
            background: `linear-gradient(135deg, ${P}10, ${C}08)`,
            border: `1px solid ${P}30`,
          }}>
          <div style={{ position: 'absolute', top: -40, right: -40, width: 160, height: 160, borderRadius: '50%', background: `${P}06`, pointerEvents: 'none' }}/>
          <div style={{ position: 'absolute', bottom: -30, left: -30, width: 120, height: 120, borderRadius: '50%', background: `${C}06`, pointerEvents: 'none' }}/>

          <div style={{ position: 'relative' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 24 }}>
              <div style={{
                width: 42, height: 42, borderRadius: 10,
                background: `${P}14`, border: `1px solid ${P}30`,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                <Gift size={20} style={{ color: P2 }}/>
              </div>
              <div>
                <h3 style={{ fontSize: 15, fontWeight: 700, color: '#fff', margin: 0 }}>Your Referral Link</h3>
                <p style={{ fontSize: 12, color: '#64748B', margin: 0 }}>Share & earn instantly</p>
              </div>
            </div>

            {/* Code */}
            <div style={{ marginBottom: 14 }}>
              <p style={{ fontSize: 10, color: '#64748B', margin: '0 0 8px', fontWeight: 700, letterSpacing: 1, textTransform: 'uppercase' }}>Referral Code</p>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <div style={{ flex: 1, ...INNER, padding: '12px 16px' }}>
                  <p style={{ fontSize: 22, fontWeight: 900, color: P2, letterSpacing: '0.3em', margin: 0 }}>{info?.referral_code}</p>
                </div>
                <motion.button whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}
                  onClick={copyCode}
                  style={{
                    padding: '12px 16px', borderRadius: 10, cursor: 'pointer', border: 'none',
                    background: copiedCode ? G : 'rgba(255,255,255,0.06)',
                    color: copiedCode ? '#fff' : '#64748B',
                    display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, fontWeight: 600,
                  }}>
                  {copiedCode ? <CheckCircle size={14}/> : <Copy size={14}/>}
                  {copiedCode ? 'Copied!' : 'Copy'}
                </motion.button>
              </div>
            </div>

            {/* Link */}
            <div style={{ marginBottom: 20 }}>
              <p style={{ fontSize: 10, color: '#64748B', margin: '0 0 8px', fontWeight: 700, letterSpacing: 1, textTransform: 'uppercase' }}>Referral Link</p>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <div style={{ flex: 1, ...INNER, padding: '12px 16px', overflow: 'hidden' }}>
                  <p style={{ fontSize: 13, color: '#CBD5E1', margin: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{info?.referral_link}</p>
                </div>
                <motion.button whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}
                  onClick={copyLink}
                  style={{
                    padding: '12px 16px', borderRadius: 10, cursor: 'pointer',
                    background: copiedLink ? G : `${P}14`,
                    border: copiedLink ? 'none' : `1px solid ${P}30`,
                    color: copiedLink ? '#fff' : P2,
                    display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, fontWeight: 600,
                  }}>
                  {copiedLink ? <CheckCircle size={14}/> : <Copy size={14}/>}
                  {copiedLink ? 'Copied!' : 'Copy'}
                </motion.button>
              </div>
            </div>

            <motion.button whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}
              onClick={shareLink}
              style={{
                width: '100%', padding: '13px 0', borderRadius: 10, border: 'none', cursor: 'pointer',
                background: `linear-gradient(135deg,${P},${P2})`,
                color: '#000', fontSize: 14, fontWeight: 700,
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
              }}>
              <Share2 size={16}/> Share Referral Link
            </motion.button>
          </div>
        </motion.div>

        {/* Earn Per Plan */}
        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          style={{ ...CARD, padding: 20, marginBottom: 20 }}>
          <p style={{ fontSize: 13, fontWeight: 700, color: '#fff', margin: '0 0 16px', display: 'flex', alignItems: 'center', gap: 8 }}>
            <TrendingUp size={14} style={{ color: P2 }}/> Earn Per Plan
          </p>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 10 }}>
            {[
              { plan: 'Starter', price: 29,  commission: 2.9, color: G  },
              { plan: 'Pro',     price: 59,  commission: 5.9, color: P2, popular: true },
              { plan: 'Elite',   price: 99,  commission: 9.9, color: C  },
            ].map((p, i) => (
              <motion.div key={i} whileHover={{ scale: 1.03 }}
                style={{
                  borderRadius: 10, padding: '16px 12px', textAlign: 'center', position: 'relative', overflow: 'hidden',
                  background: p.popular ? `${p.color}08` : 'rgba(255,255,255,0.02)',
                  border: `1px solid ${p.popular ? `${p.color}30` : 'rgba(255,255,255,0.06)'}`,
                }}>
                {p.popular && (
                  <div style={{
                    position: 'absolute', top: 0, left: 0, right: 0, height: 2,
                    background: `linear-gradient(90deg,${p.color},${p.color}80)`,
                  }}/>
                )}
                <p style={{ fontSize: 11, color: '#64748B', margin: '0 0 2px', fontWeight: 600 }}>{p.plan}</p>
                <p style={{ fontSize: 11, color: '#334155', margin: '0 0 10px' }}>${p.price}/mo</p>
                <p style={{ fontSize: 22, fontWeight: 900, color: p.color, margin: '0 0 2px' }}>${p.commission}</p>
                <p style={{ fontSize: 11, color: '#475569', margin: 0 }}>per referral</p>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* Tabs */}
        <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap' }}>
          {[
            { id: 'overview',    label: 'How It Works' },
            { id: 'referrals',   label: `Referrals (${info?.referrals?.length || 0})` },
            { id: 'commissions', label: `Earnings (${commissions.length})` },
          ].map(t => (
            <button key={t.id} onClick={() => setActiveTab(t.id)}
              style={{
                padding: '9px 16px', borderRadius: 10, fontSize: 13, fontWeight: 600, cursor: 'pointer',
                background: activeTab === t.id ? `linear-gradient(135deg,${P},${P2})` : 'rgba(255,255,255,0.04)',
                border: activeTab === t.id ? 'none' : '1px solid rgba(255,255,255,0.08)',
                color: activeTab === t.id ? '#000' : '#64748B',
              }}>{t.label}</button>
          ))}
        </div>

        <AnimatePresence mode="wait">

          {activeTab === 'overview' && (
            <motion.div key="overview"
              initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }}
              style={{ ...CARD, padding: 24 }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                {[
                  { step: '01', title: 'Share Your Link',      desc: 'Copy your unique referral link and share with friends or on social media!',                  color: P2 },
                  { step: '02', title: 'Friend Registers',     desc: 'Your friend clicks the link and creates a Kudibot account using your referral code!',        color: G  },
                  { step: '03', title: 'Friend Subscribes',    desc: 'When your friend subscribes to any plan, your commission is calculated instantly!',           color: C  },
                  { step: '04', title: 'You Earn Commission!', desc: '10% commission is added to your wallet automatically. Withdraw anytime!',                    color: P2 },
                ].map((item, i) => (
                  <motion.div key={i}
                    initial={{ opacity: 0, x: -12 }} animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: i * 0.08 }}
                    style={{ display: 'flex', alignItems: 'flex-start', gap: 14, ...INNER, padding: '14px 16px' }}>
                    <div style={{
                      width: 38, height: 38, borderRadius: 10, flexShrink: 0,
                      background: `${item.color}14`, border: `1px solid ${item.color}30`,
                      color: item.color, display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontSize: 12, fontWeight: 800,
                    }}>{item.step}</div>
                    <div>
                      <p style={{ fontSize: 13, fontWeight: 700, color: '#fff', margin: '0 0 4px' }}>{item.title}</p>
                      <p style={{ fontSize: 12, color: '#64748B', margin: 0, lineHeight: 1.5 }}>{item.desc}</p>
                    </div>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          )}

          {activeTab === 'referrals' && (
            <motion.div key="referrals"
              initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }}
              style={{ ...CARD, padding: 24 }}>
              {!info?.referrals?.length ? (
                <div style={{ padding: '40px 0', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10 }}>
                  <div style={{
                    width: 52, height: 52, borderRadius: 14,
                    background: 'rgba(255,255,255,0.04)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}>
                    <Users size={24} style={{ color: '#334155' }}/>
                  </div>
                  <p style={{ fontSize: 14, color: '#475569', fontWeight: 600, margin: 0 }}>No referrals yet</p>
                  <p style={{ fontSize: 12, color: '#334155', margin: 0 }}>Share your link to start earning!</p>
                  <motion.button whileHover={{ scale: 1.04 }} onClick={shareLink}
                    style={{
                      marginTop: 4, padding: '9px 20px', borderRadius: 10, cursor: 'pointer',
                      background: `${P}14`, border: `1px solid ${P}30`,
                      color: P2, fontSize: 13, fontWeight: 600,
                    }}>
                    Share Now →
                  </motion.button>
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {info.referrals.map((ref, i) => (
                    <motion.div key={i}
                      initial={{ opacity: 0, x: -12 }} animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: i * 0.05 }}
                      style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', ...INNER, padding: '14px 16px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                        <div style={{
                          width: 38, height: 38, borderRadius: 10,
                          background: `${P}14`, border: `1px solid ${P}30`,
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          fontSize: 14, fontWeight: 800, color: P2,
                        }}>{ref.name?.charAt(0).toUpperCase()}</div>
                        <div>
                          <p style={{ fontSize: 13, fontWeight: 600, color: '#fff', margin: '0 0 2px' }}>{ref.name}</p>
                          <p style={{ fontSize: 11, color: '#475569', margin: '0 0 1px' }}>{ref.email}</p>
                          <p style={{ fontSize: 10, color: '#334155', margin: 0 }}>{new Date(ref.joined).toLocaleDateString()}</p>
                        </div>
                      </div>
                      <div style={{ textAlign: 'right' }}>
                        <p style={{ fontSize: 14, fontWeight: 800, color: G, margin: '0 0 4px' }}>${ref.commission}</p>
                        <span style={{
                          fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 5,
                          background: ref.status === 'paid' ? `${G}14` : `${'#F59E0B'}14`,
                          color: ref.status === 'paid' ? G : '#F59E0B',
                        }}>{ref.status}</span>
                      </div>
                    </motion.div>
                  ))}
                </div>
              )}
            </motion.div>
          )}

          {activeTab === 'commissions' && (
            <motion.div key="commissions"
              initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }}
              style={{ ...CARD, padding: 24 }}>
              {!commissions.length ? (
                <div style={{ padding: '40px 0', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10 }}>
                  <div style={{
                    width: 52, height: 52, borderRadius: 14,
                    background: 'rgba(255,255,255,0.04)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}>
                    <DollarSign size={24} style={{ color: '#334155' }}/>
                  </div>
                  <p style={{ fontSize: 14, color: '#475569', fontWeight: 600, margin: 0 }}>No commissions yet</p>
                  <p style={{ fontSize: 12, color: '#334155', margin: 0 }}>Commissions appear when referrals subscribe</p>
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {commissions.map((c, i) => (
                    <motion.div key={c.id}
                      initial={{ opacity: 0, x: -12 }} animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: i * 0.05 }}
                      style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', ...INNER, padding: '14px 16px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                        <div style={{
                          width: 38, height: 38, borderRadius: 10,
                          background: `${G}14`, border: `1px solid ${G}30`,
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                        }}>
                          <DollarSign size={16} style={{ color: G }}/>
                        </div>
                        <div>
                          <p style={{ fontSize: 13, fontWeight: 600, color: '#fff', margin: '0 0 2px' }}>{c.referred}</p>
                          <p style={{ fontSize: 11, color: '#475569', margin: '0 0 1px', textTransform: 'capitalize' }}>{c.plan} plan</p>
                          <p style={{ fontSize: 10, color: '#334155', margin: 0 }}>{new Date(c.created_at).toLocaleDateString()}</p>
                        </div>
                      </div>
                      <div style={{ textAlign: 'right' }}>
                        <p style={{ fontSize: 16, fontWeight: 900, color: G, margin: '0 0 4px' }}>+${c.amount_usdt}</p>
                        <span style={{
                          fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 5,
                          background: c.status === 'paid' ? `${G}14` : `${'#F59E0B'}14`,
                          color: c.status === 'paid' ? G : '#F59E0B',
                        }}>{c.status}</span>
                      </div>
                    </motion.div>
                  ))}
                </div>
              )}
            </motion.div>
          )}
        </AnimatePresence>

      </div>

      <style>{`
        @media (max-width: 900px) { .ref-stats { grid-template-columns: repeat(2,1fr) !important; } }
        @keyframes spin { to { transform: rotate(360deg); } }
      `}</style>
    </Layout>
  );
}
